import type {ConferenceApiModel} from '../../../../api/conference';
import type {CourseApiModel} from '../../../../api/course';
import type {
  EventApiModel,
  EventType,
  NotificationEvent,
} from '../../../../api/event';
import type {GoalApiModel} from '../../../../api/goal';
import type {InviteApiModel} from '../../../../api/invite';
import type {MembershipApiModel} from '../../../../api/membership';
import type {PaymentApiModel} from '../../../../api/payment';
import type {ProgramApiModel} from '../../../../api/program';
import type {BNBoundFormApiModel} from '../../../../api/types/bnrks/models';
import type {ApiRpcCursor} from '../../../../api/utils/apiRequest';
import type {CoreOrB2bEnv} from '../../../../utils/coreOrB2b/types';

import {currentUserStore} from '../../../../api/user';
import {envManager} from '../../../../utils/env';
import {findContext} from '../../utils';

export function getExcludedEvents(env?: CoreOrB2bEnv): EventApiModel['type'][] {
  const isCore = env ? env === 'core' : envManager.isCore;

  const events: EventApiModel['type'][] = [
    'post_created',
    'post_deleted',
    'post_updated',
    'program_deleted',
    'points_added',
    'tier_upgraded',
    'tier_downgraded',
    'coach_blocked',
    'coach_unblocked',
    'payment_expiring',
    'subscription_info',
    'file_converted',
    'form_was_deleted',
  ];

  if (isCore) {
    // we've replaced it with 'conference_scheduled'
    events.push('conference_booked');

    events.push('conference_reminder');
    events.push('unread_post_reminder');
    events.push('call_status_updated');
  }

  // todo: Comment out to work with non-stage1 backend
  events.push('audio_transcription_completed');

  return events;
}

export const TODAY_EVENTS_LIMIT = 200;

export const LIMIT = 50;

export const DEFAULT_CURSOR: ApiRpcCursor = [-1, 0, null];

export const filterEvent = (event: NotificationEvent) => {
  if (event.type.includes('payment')) {
    const payment = findContext<PaymentApiModel>(event, 'payment');

    if (!payment) {
      return false;
    }

    if (
      event.type === 'payment_requires_action' ||
      event.type === 'payment_failed'
    ) {
      if (payment.paid) {
        return false;
      }
    }

    if (payment.type === 'offer') {
      return false;
    }
  }

  if (
    (
      ['program_shared', 'program_signed', 'program_resigned'] as EventType[]
    ).includes(event.type)
  ) {
    const program = findContext<ProgramApiModel>(event, 'program');

    if (!program) {
      return false;
    }

    if (
      event.type === 'program_shared' &&
      currentUserStore.user &&
      program.coaches[currentUserStore.user._id] &&
      program.coaches[currentUserStore.user._id].signed
    ) {
      return false;
    }

    if (
      event.type === 'program_signed' &&
      Object.keys(program.coaches).length === 1
    ) {
      return false;
    }

    if (
      event.type === 'program_resigned' &&
      currentUserStore.user?._id === program.user_id
    ) {
      return false;
    }
  }

  if (event.type.includes('conference')) {
    const conference = findContext<ConferenceApiModel>(event, 'conference');

    if (!conference) {
      return false;
    }

    if (conference.status === 'rescheduled') {
      return false;
    }
  }

  if (
    (
      ['edition_signed', 'edition_created', 'course_created'] as EventType[]
    ).includes(event.type)
  ) {
    const program = findContext<ProgramApiModel>(event, 'program');

    if (!program) {
      return false;
    }
  }

  if (event.type === 'invite_received') {
    const invite = findContext<InviteApiModel>(event, 'invite');

    if (!invite) {
      return false;
    }
  }

  if (event.type === 'course_created') {
    const course = findContext<CourseApiModel>(event, 'course');

    if (!course) {
      return false;
    }
  }

  if (event.type === 'membership_proposed') {
    const membership = findContext<MembershipApiModel>(event, 'membership');

    if (!membership || membership.expired || membership.status !== 'proposed') {
      return false;
    }
  }

  if (event.type === 'membership_requested') {
    const membership = findContext<MembershipApiModel>(event, 'membership');

    if (!membership || membership.status !== 'requested') {
      return false;
    }
  }

  if (
    event.type === 'goal_coach_review_reminder' ||
    event.type === 'goal_coach_review_expiring' ||
    event.type === 'goal_client_review_reminder' ||
    event.type === 'goal_client_review_expiring'
  ) {
    const goal = findContext<GoalApiModel>(event, 'goal');

    if (goal && goal.status === 'archived') {
      return false;
    }
  }

  if (event.type === 'bound_form_missed') {
    const boundForm = findContext<BNBoundFormApiModel>(event, 'bound_form');

    if (boundForm?.answered) {
      return false;
    }
  }

  return true;
};
